import React, { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  HomeIcon, TicketIcon, BookOpenIcon, CogIcon,
  Bars3Icon, XMarkIcon, ArrowRightOnRectangleIcon, UserCircleIcon,
  ChartBarIcon, PlusIcon, ChevronDownIcon,
  ChevronRightIcon, UserGroupIcon, DocumentChartBarIcon,
  MagnifyingGlassIcon, ChatBubbleLeftRightIcon, EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import SearchModal from '../common/SearchModal';
import NotificationsDropdown from '../common/NotificationsDropdown';
import LiveChatWidget from '../common/LiveChatWidget';
import LanguageSwitcher from '../common/LanguageSwitcher';
import DarkModeToggle from '../common/DarkModeToggle';
import CommandPalette from '../common/CommandPalette';

/* ─── breadcrumb map ─────────────────────────────────────────────────────── */
const CRUMB_MAP = {
  '/dashboard':      'Dashboard',
  '/tickets':        'My Tickets',
  '/all-tickets':    'All Tickets',
  '/tickets/new':    'New Ticket',
  '/knowledge-base': 'Knowledge Base',
  '/users':          'Users',
  '/reports':        'Reports',
  '/settings':       'Settings',
  '/profile':        'My Profile',
  '/live-chat':      'Live Chat',
};

const useBreadcrumbs = () => {
  const { pathname } = useLocation();
  const parts = pathname.split('/').filter(Boolean);
  const crumbs = [];
  let built = '';
  parts.forEach((part, i) => {
    built += '/' + part;
    crumbs.push({
      label: CRUMB_MAP[built] || (part.length === 24 ? '…' : part.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())),
      path: built,
      isLast: i === parts.length - 1,
    });
  });
  return crumbs;
};

/* ─── User Dropdown ──────────────────────────────────────────────────────── */
const UserDropdown = ({ user, onLogout }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() || '?';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2.5 px-2 py-1.5 rounded-xl hover:bg-[#e8f7fc] transition-colors"
      >
        <div
          className="w-8 h-8 rounded-full text-white text-xs font-bold flex items-center justify-center select-none flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))' }}
        >
          {user?.avatar
            ? <img src={user.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
            : initials}
        </div>
        <div className="hidden sm:block text-left">
          <p className="text-[13px] font-semibold text-slate-800 leading-tight">{user?.firstName} {user?.lastName}</p>
          <p className="text-[11px] text-slate-400 capitalize">{user?.role}</p>
        </div>
        <ChevronDownIcon className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="dropdown-menu right-0 top-full mt-2 animate-scale-in" onClick={() => setOpen(false)}>
          <div className="px-4 py-3 border-b border-gray-100 mb-0.5">
            <p className="text-sm font-semibold text-slate-900">{user?.firstName} {user?.lastName}</p>
            <p className="text-xs text-slate-400 truncate mt-0.5">{user?.email}</p>
          </div>
          <button className="dropdown-item w-full" onClick={() => navigate('/profile')}>
            <UserCircleIcon className="w-4 h-4 text-gray-400" />
            My Profile
          </button>
          <button className="dropdown-item w-full danger separator mt-1" onClick={onLogout}>
            <ArrowRightOnRectangleIcon className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

/* ─── Main Layout ────────────────────────────────────────────────────────── */
const DashboardLayout = () => {
  const { appName, labels, features, logoUrl, whiteLabelEnabled } = useTenant();
  const { user, logout, isAgent, isAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen]   = useState(false);
  const navigate = useNavigate();
  const crumbs = useBreadcrumbs();

  /* open search on "/" keypress outside input fields */
  useEffect(() => {
    const handler = (e) => {
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/dashboard',    icon: HomeIcon,             label: labels.dashboard,    section: null },
    { to: '/tickets',      icon: TicketIcon,           label: labels.myTickets,    section: 'Tickets' },
    ...(isAgent ? [
      { to: '/all-tickets',icon: ChartBarIcon,         label: 'All Tickets',       section: 'Tickets' },
    ] : []),
    { to: '/tickets/new',  icon: PlusIcon,             label: labels.submitTicket, section: 'Tickets' },
    ...(features.knowledgeBase ? [
      { to: '/knowledge-base', icon: BookOpenIcon,              label: labels.knowledgeBase, section: 'Resources' },
    ] : []),
    ...(features.liveChat && isAgent ? [
      { to: '/live-chat', icon: ChatBubbleLeftRightIcon, label: 'Live Chat', section: 'Resources' },
    ] : []),
    ...(isAgent && !isAdmin ? [
      { to: '/reports', icon: DocumentChartBarIcon,   label: 'Reports',           section: 'Reports' },
    ] : []),
    ...(isAgent ? [
      { to: '/ticket-templates', icon: TicketIcon,    label: 'Templates',         section: 'Resources' },
    ] : []),
    ...(isAdmin ? [
      { to: '/users',    icon: UserGroupIcon,         label: 'Users',             section: 'Admin' },
      { to: '/agent-groups', icon: UserGroupIcon,     label: 'Support Teams',     section: 'Admin' },
      { to: '/categories', icon: CogIcon,             label: 'Categories',        section: 'Admin' },
      { to: '/email-templates', icon: EnvelopeIcon,   label: 'Email Templates',   section: 'Admin' },
      { to: '/reports',  icon: DocumentChartBarIcon,  label: 'Reports',           section: 'Admin' },
      { to: '/webhooks', icon: CogIcon,               label: 'Webhooks',          section: 'Admin' },
      { to: '/audit-logs', icon: DocumentChartBarIcon, label: 'Audit Logs',       section: 'Admin' },
      { to: '/settings', icon: CogIcon,               label: 'Settings',          section: 'Admin' },
    ] : []),
  ];

  const sections = ['', 'Tickets', 'Resources', 'Reports', 'Admin'];
  const grouped = sections.map((s) => ({
    section: s,
    items: navItems.filter((n) => (n.section || '') === s),
  })).filter((g) => g.items.length > 0);

  const SidebarContent = () => (
    <div className="flex flex-col h-full" style={{
      background: 'linear-gradient(180deg, #0d1955 0%, #12206b 50%, #0a1648 100%)',
    }}>

      {/* Brand */}
      <div className="px-4 py-5 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 select-none"
            style={{ background: '#ffffff', border: '1px solid rgba(0,180,216,0.35)', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
          >
{logoUrl ? (
              <img
                src={logoUrl}
                alt={appName}
                className="h-12 w-12 object-contain rounded-xl"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
            ) : null}
            <span
              className="text-white font-extrabold text-sm leading-none"
              style={{ display: logoUrl ? 'none' : 'flex' }}
            >
              {(appName || 'U')[0].toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <h1
              className="font-bold text-[14px] leading-tight truncate"
              style={{ color: 'rgba(255,255,255,0.95)' }}
            >
              {appName}
            </h1>
            <p className="text-[11px] truncate" style={{ color: 'rgba(255,255,255,0.38)' }}>
              {labels.supportPortal}
            </p>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="mx-4 mb-3" style={{ height: '1px', background: 'rgba(255,255,255,0.07)' }} />

      {/* Nav */}
      <nav className="flex-1 px-2 overflow-y-auto scrollbar-none space-y-px">
        {grouped.map(({ section, items }) => (
          <div key={section}>
            {section && (
              <p className="sidebar-section">{section}</p>
            )}
            {items.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/dashboard'}
                className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <span className="icon-wrap">
                  <Icon className="w-[17px] h-[17px]" />
                </span>
                <span>{label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* Powered by Tzar Help Desk badge */}
      {!whiteLabelEnabled && (
        <div className="mx-4 mb-2 flex items-center justify-center gap-1 flex-shrink-0">
          <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.28)' }}>
            Powered by{' '}
            <span style={{ color: 'rgba(0,180,216,0.85)', fontWeight: 600 }}>Tzar Help Desk</span>
          </p>
        </div>
      )}

      {/* Bottom user strip */}
      <div
        className="mx-4 mt-2 mb-4 rounded-2xl p-3 flex items-center gap-2.5 flex-shrink-0"
        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(0,180,216,0.22)' }}
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 select-none"
          style={{ background: 'linear-gradient(135deg, #12206b, #0d1a56)', boxShadow: '0 2px 8px rgba(18,32,107,0.5)' }}
        >
          {`${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-[12px] font-semibold truncate leading-tight"
            style={{ color: 'rgba(255,255,255,0.90)' }}
          >
            {user?.firstName} {user?.lastName}
          </p>
          <p className="text-[10px] capitalize truncate" style={{ color: 'rgba(255,255,255,0.40)' }}>
            {user?.role}
          </p>
        </div>
        <button
          onClick={handleLogout}
          className="p-1.5 rounded-lg transition-colors flex-shrink-0"
          style={{ color: 'rgba(255,255,255,0.40)' }}
          title="Sign out"
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'rgba(255,255,255,0.9)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; e.currentTarget.style.color = 'rgba(255,255,255,0.40)'; }}
        >
          <ArrowRightOnRectangleIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Helmet><title>{appName} — Support</title></Helmet>

      {/* Skip to main content link (accessibility) */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[200] focus:bg-white focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:text-primary focus:font-semibold focus:text-sm">
        Skip to main content
      </a>

      <div className="flex h-screen overflow-hidden" style={{ backgroundColor: 'var(--color-bg)' }}>

        {/* Desktop sidebar */}
        <aside className="hidden md:flex w-[228px] flex-col flex-shrink-0 z-10" style={{ boxShadow: '1px 0 0 0 rgba(0,0,0,0.07), 4px 0 16px -4px rgba(0,0,0,0.08)' }} role="navigation" aria-label="Main navigation">
          <SidebarContent />
        </aside>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-50 md:hidden flex">
            <div
              className="absolute inset-0"
              style={{ background: 'rgba(10,16,32,0.55)', backdropFilter: 'blur(4px)' }}
              onClick={() => setSidebarOpen(false)}
            />
            <aside
              className="relative w-[228px] flex-shrink-0 z-10 animate-slide-up"
              style={{ boxShadow: 'var(--shadow-xl)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <SidebarContent />
            </aside>
            <button
              className="absolute top-4 left-[240px] bg-white/90 backdrop-blur rounded-full p-1.5 z-10"
              style={{ boxShadow: 'var(--shadow-md)' }}
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="w-5 h-5 text-gray-700" />
            </button>
          </div>
        )}

        {/* Content area */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

          {/* Top Header Bar */}
          <header className="topbar flex-shrink-0">
            {/* Left: hamburger + breadcrumbs */}
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden btn-icon btn-ghost"
              >
                <Bars3Icon className="w-5 h-5" />
              </button>

              <nav className="hidden sm:flex items-center gap-1" aria-label="Breadcrumb">
                {crumbs.map((crumb, i) => (
                  <React.Fragment key={crumb.path}>
                    {i > 0 && (
                      <ChevronRightIcon className="w-3 h-3 text-gray-300 flex-shrink-0" />
                    )}
                    <span
                      className={`text-sm truncate ${
                        crumb.isLast
                          ? 'text-gray-800 font-semibold'
                          : 'text-gray-400 hover:text-gray-600 cursor-pointer transition-colors'
                      }`}
                    >
                      {crumb.label}
                    </span>
                  </React.Fragment>
                ))}
              </nav>

              <span className="sm:hidden font-semibold text-gray-900 text-sm truncate">
                {crumbs.at(-1)?.label || appName}
              </span>
            </div>

            {/* Right actions */}
            <div className="topbar-actions">
              {/* Quick search button */}
              <button
                onClick={() => setSearchOpen(true)}
                className="hidden sm:flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-slate-400 hover:bg-white transition-all duration-150 cursor-pointer"
                style={{ minWidth: '180px', backgroundColor: '#f8f9fc', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-xs)' }}
                title="Press / to search"
              >
                <MagnifyingGlassIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#00b4d8' }} />
                <span className="text-xs font-medium">Quick search…</span>
                <kbd className="ml-auto text-[10px] bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded font-mono">/</kbd>
              </button>

              {/* New Ticket */}
              <button
                onClick={() => navigate('/tickets/new')}
                className="btn-primary btn-sm hidden sm:inline-flex gap-1.5"
              >
                <PlusIcon className="w-3.5 h-3.5" />
                New Ticket
              </button>

              {/* Notifications */}
              <NotificationsDropdown />

              {/* Language switcher — always visible */}
              <LanguageSwitcher />

              {/* Dark mode toggle */}
              <DarkModeToggle />

              <UserDropdown user={user} onLogout={handleLogout} />
            </div>
          </header>

          {/* Page content */}
          <main id="main-content" className="flex-1 overflow-y-auto" role="main">
            <div className="w-full px-4 sm:px-6 lg:px-8 py-6 animate-fade-in">
              <Outlet />
            </div>
          </main>

          {/* Global search modal */}
          <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />

          {/* Live chat widget (customers only, shown when feature enabled) */}
          <LiveChatWidget />

          {/* Command palette (Ctrl+K / Cmd+K) */}
          <CommandPalette />
        </div>
      </div>
    </>
  );
};

export default DashboardLayout;
